import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useQuery, useSubscription } from '@apollo/client';
import { list, patientFileAsync, patientFileReadySubscription } from '../../../graph/surgeon/forms';
import groupBy from 'lodash/groupBy';
import Hidden from '@material-ui/core/Hidden';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import DialogContentText from '@material-ui/core/DialogContentText';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Box from '@material-ui/core/Box';
import FormGroup from '@material-ui/core/FormGroup';
import Typography from '@material-ui/core/Typography';
import { formSaveTypeLabels } from '../procedures/enums';
import DialogActions from '@material-ui/core/DialogActions';
import Spinner from '@/se/components/Spinner';
import { FormType } from '../../../types/Form';
import { useSession } from '../../../state/Session';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import Tooltip from '../../Tooltip';

const IntakePackageGenerator = ({ procedureId }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [waitingForFile, setWaitingForFile] = useState(false);

  const [state, setState] = useState({});
  const { data: formQuery } = useQuery(list);
  const reportForms = useMemo(() => (formQuery?.forms || []).filter(e => e.type === FormType.InTakeForm), [formQuery]);

  const sections = useMemo(() => groupBy(reportForms, _ => _.saveType), [reportForms]);

  useEffect(() => {
    setState(reportForms.reduce((acc, curr) => ({ ...acc, [curr.id]: true }), {}));
  }, [reportForms]);

  const anyChecked = useMemo(() => Object.values(state).includes(true), [state]);

  const client = useApolloClient();

  const session = useSession();
  const { isSuperAdmin } = unpackSessionObject(session);

  // Subscribe to patient file ready events when dialog is open
  // Start subscription early to avoid race condition where error is published before subscription establishes
  const { data: fileReadyData, error: subscriptionError } = useSubscription(patientFileReadySubscription, {
    variables: { procedureId },
    skip: !open, // Subscribe when dialog is open, not just when waiting
  });

  // Handle subscription errors
  useEffect(() => {
    if (subscriptionError && waitingForFile) {
      setErrorMessage(`Connection error: ${subscriptionError.message}`);
      setWaitingForFile(false);
      setBusy(false);
    }
  }, [subscriptionError, waitingForFile]);

  // Timeout for file generation (2 minutes)
  useEffect(() => {
    if (!waitingForFile) return;

    const timeout = setTimeout(() => {
      if (waitingForFile) {
        setErrorMessage('PDF generation timed out. Please try again.');
        setWaitingForFile(false);
        setBusy(false);
      }
    }, 120000);

    return () => clearTimeout(timeout);
  }, [waitingForFile]);

  // Handle subscription data when file is ready
  useEffect(() => {
    if (waitingForFile && fileReadyData) {
      const result = fileReadyData.patientFileReady;

      if (result) {
        const { url, error } = result;

        if (url) {
          // Success - download the file
          const link = document.createElement('a');
          link.download = 'PatientFile' + procedureId + Date.now() + '.pdf';
          link.target = '_blank';
          link.href = url;
          link.text = 'PDF download';
          link.dispatchEvent(new MouseEvent('click'));
          URL.revokeObjectURL(link.href);

          // Reset state
          setWaitingForFile(false);
          setBusy(false);
          setOpen(false);
          setErrorMessage(null);
        } else if (error) {
          // Error from backend - show message
          setErrorMessage(`Failed to generate PDF: ${error}`);
          setWaitingForFile(false);
          setBusy(false);
        }
      }
    }
  }, [fileReadyData, waitingForFile, procedureId]);

  const handleClickOpen = () => {
    setOpen(true);
    setErrorMessage(null);
  };

  const handleGenerate = useCallback(async () => {
    if (anyChecked) {
      setBusy(true);
      setErrorMessage(null);

      try {
        const formIds = Object.entries(state)
          .filter(([_, value]) => !!value)
          .map(([key, _]) => parseInt(key, 10))
          .sort((a, b) => reportForms.find(x => x.id === a).order - reportForms.find(x => x.id === b).order);

        // Start async generation - returns immediately with "processing"
        const result = await client.query({
          query: patientFileAsync,
          variables: { procedureId, ids: formIds, mdSignature: false },
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        });

        // Check for GraphQL errors in the result
        if (result.errors && result.errors.length > 0) {
          const errorMessages = result.errors.map(e => e.message).join('; ');
          setErrorMessage(`Error: ${errorMessages}`);
          setBusy(false);
          return;
        }

        if (result.data?.patientFileAsync === 'processing') {
          // Now wait for the subscription to deliver the URL
          setWaitingForFile(true);
        } else {
          // Unexpected response
          setErrorMessage('Unexpected response from server');
          setBusy(false);
        }
      } catch (error) {
        // Handle network errors or unexpected exceptions
        const message = error.message || 'Unknown error occurred';
        setErrorMessage(`Error: ${message}`);
        setBusy(false);
      }
    }
  }, [anyChecked, state, reportForms, client, procedureId]);

  const handleCancel = () => {
    setOpen(false);
    setWaitingForFile(false);
    setBusy(false);
    setErrorMessage(null);
  };

  const handleCheckboxChange = (id, checked) => {
    setState({ ...state, [id]: checked });
  };

  const selectAll = () => {
    setState(Object.keys(state).reduce((acc, curr) => ({ ...acc, [curr]: true }), {}));
  };

  const deselectAll = () => {
    setState(Object.keys(state).reduce((acc, curr) => ({ ...acc, [curr]: false }), {}));
  };

  return (
    <Fragment>
      {reportForms.length > 0 && (
        <Box display="flex" flexDirection="row" justifyContent="flex-end" mt={2}>
          <Hidden smDown>
            <Tooltip content={isSuperAdmin ? "You don't have sufficient permissions to generate this document." : null}>
              <Button width={200} color="primary" onClick={handleClickOpen} disabled={isSuperAdmin}>
                Generate Registration Package
              </Button>
            </Tooltip>
          </Hidden>
        </Box>
      )}
      <Dialog fullWidth={true} open={open} onClose={handleCancel} aria-labelledby="form-dialog-title">
        <DialogTitle>Generate Registration Package</DialogTitle>
        <DialogContent>
          <DialogContentText>Please select sections for patient file.</DialogContentText>
          {errorMessage && (
            <Box mt={2} p={2} bgcolor="error.main" color="error.contrastText" borderRadius={1}>
              <Typography variant="body2">{errorMessage}</Typography>
            </Box>
          )}
          <Box mt={2} display="flex" flexDirection="row" width="100%">
            <FormGroup style={{ width: '100%' }}>
              <Grid container>
                <Grid item xs={12}>
                  <Box mb={2}>
                    <Button color="primary" size="small" onClick={selectAll}>
                      Select all
                    </Button>
                    <Button color="primary" size="small" onClick={deselectAll}>
                      Deselect all
                    </Button>
                  </Box>
                </Grid>
                {Object.entries(sections)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([type, items], index) => (
                    <Grid item md={6} key={`section-patient-file-generator-${index}`}>
                      <Typography variant="subtitle1" gutterBottom>
                        {formSaveTypeLabels[type]}
                      </Typography>
                      <Box display="flex" flexDirection="column">
                        {items.map(item => (
                          <FormControlLabel
                            key={`${type}-${item.id}`}
                            control={
                              <Checkbox
                                checked={state[item.id]}
                                onChange={e => handleCheckboxChange(item.id, e.target.checked)}
                                name={item.name}
                              />
                            }
                            label={item.name}
                          />
                        ))}
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button disabled={busy || !anyChecked} onClick={handleGenerate} color="primary">
            {waitingForFile ? 'Generating...' : 'Generate & Download'}
          </Button>

          {busy && (
            <Spinner
              freezeAnimation={!open}
              style={{
                verticalAlign: 'middle',
                marginLeft: '0.5rem',
              }}
            />
          )}
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default IntakePackageGenerator;
